/**
 * Developer API: Artist Shows fetch + pagination
 *
 * Backs GET /api/artists/{artistId}/shows. The sibling `comedian-shows.ts`
 * owns the default-deny transform; this module owns getting ONE page of rows
 * out of the database without paying for the whole listing.
 *
 * WHY NOT `getShows` + slice: that fetcher hydrates every upcoming show with
 * full nested artist rows (bios, images) and every venue-page join, then the
 * caller throws all but `perPage` of them away - so `perPage=1` cost the same
 * as `perPage=100`. Two of the visibility rules also cannot be expressed as
 * plain row filters, so a naive `.range()` on that query would return short
 * pages and an inflated `total`.
 *
 * The shape here is candidates -> filter -> hydrate one page:
 *
 *  1. CANDIDATES - one narrow query returning ids plus only the columns the
 *     visibility rules read. Everything expressible in SQL is pushed down:
 *     the artist's own `hidden_from_comedian_page` pairing flag, the
 *     upcoming-datetime window, and scheduled `published_at`.
 *  2. FILTER - the two rules SQL cannot express, applied over those narrow
 *     rows. `showCanBeRendered` is a FOR-ALL over the lineup (a join filter
 *     is an EXISTS, so it would keep shows it must drop), and password
 *     protection lives behind a Tixologi call, injected here because
 *     @punchup/tixologi depends on this package and not the other way round.
 *  3. HYDRATE - the surviving ids are the exact, ordered result set, so
 *     `total` stays exact and only the requested page is ever hydrated.
 *
 * The heavy join is therefore bounded by `perPage`; only the two narrow
 * queries and the single bulk upstream call scale with the artist's listing.
 */

import { logSupabaseException } from "@punchup/sentry";
import dayjs from "@punchup/utils/dayjs";

import createNoAuthSupabaseClient from "../../supabase/supabase";
import { ShowWithComedian, getShowsByIds } from "../../legacy/shows";
import { DeveloperApiPagination } from "../../models/developer-api.types";
import { HiddenEventIdResolver, isPubliclyVisible } from "./shared";

/**
 * How far back the listing reaches. Matches `getShows`: a show that started in
 * the last 12 hours is still "on" as far as a listing is concerned.
 */
const UPCOMING_WINDOW_HOURS = 12;

export interface GetComedianShowsPageParams {
  page: number;
  perPage: number;
  resolveHiddenEventIds: HiddenEventIdResolver;
}

export interface ComedianShowsPage {
  shows: ShowWithComedian[];
  pagination: DeveloperApiPagination;
}

/** A candidate row: the id, plus only what the visibility rules read. */
interface CandidateShow {
  id: string;
  tixologiEventId: string | null;
}

/**
 * One page of an artist's upcoming public shows, in the order the listing
 * shows them (soonest first).
 *
 * @param comedianId - The artist whose listing to page through
 * @param params - Page number, page size, and the upstream hidden-event lookup
 * @returns The page's fully hydrated shows and its exact pagination metadata
 */
export async function getComedianShowsPage(
  comedianId: string,
  { page, perPage, resolveHiddenEventIds }: GetComedianShowsPageParams
): Promise<ComedianShowsPage> {
  const candidates = await fetchCandidateShows(comedianId);

  const ticketedEventIds = candidates
    .map((candidate) => candidate.tixologiEventId)
    .filter((id): id is string => !!id);
  const hiddenEventIds = await resolveHiddenEventIds(ticketedEventIds);

  const visible = candidates.filter(
    (candidate) =>
      !candidate.tixologiEventId ||
      !hiddenEventIds.has(candidate.tixologiEventId)
  );

  const start = (page - 1) * perPage;
  const pageIds = visible.slice(start, start + perPage).map((show) => show.id);

  return {
    shows: await hydrateInOrder(pageIds),
    pagination: {
      page,
      perPage,
      total: visible.length,
      hasMore: start + perPage < visible.length,
    },
  };
}

/**
 * The ordered candidate set: every upcoming show this artist is billed on and
 * not hidden from their page, minus the ones a lineup member makes non-public.
 *
 * Ordered by `datetime`, then `id` as a tiebreaker - without a total order two
 * shows at the same instant can swap places between two page requests, which
 * duplicates one row and drops another across the boundary.
 */
async function fetchCandidateShows(
  comedianId: string
): Promise<CandidateShow[]> {
  const supabase = createNoAuthSupabaseClient();
  const fetchFrom = dayjs()
    .subtract(UPCOMING_WINDOW_HOURS, "hours")
    .toISOString();
  const now = dayjs().toISOString();

  // Pre-query for the shows this artist is billed on. `hidden_from_comedian_page`
  // is a property of THIS comedian's pairing, not the show, so it belongs here
  // rather than on the show query. Mirrors getShows' date window so a prolific
  // comedian's full history never reaches the `.in()` below.
  const { data: pairings, error: pairingError } = await supabase
    .from("show_comedian_pairings")
    .select("show_id, comedian_shows!inner(datetime)")
    .eq("comedian_id", comedianId)
    .not("hidden_from_comedian_page", "is", true)
    .gte("comedian_shows.datetime", fetchFrom);

  if (pairingError) {
    logSupabaseException(pairingError, "developer API comedian show pairings", {
      tags: { comedianId },
    });
    throw new Error("Error fetching shows");
  }

  const pairedShowIds = pairings?.map((row) => row.show_id) ?? [];
  if (pairedShowIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("comedian_shows")
    .select(
      `id,
      tixologi_event_id,
      show_comedians:show_comedian_pairings(
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          is_punchup_verified,
          is_external_preview,
          published_at
        )
      )`
    )
    .in("id", pairedShowIds)
    .gte("datetime", fetchFrom)
    // Scheduled tour dates stay out of the listing until they publish.
    .or(`published_at.is.null,published_at.lte.${now}`)
    .order("datetime", { ascending: true })
    .order("id", { ascending: true });

  if (error) {
    logSupabaseException(error, "developer API comedian show candidates", {
      tags: { comedianId },
    });
    throw new Error("Error fetching shows");
  }

  return (data ?? [])
    .filter((show) =>
      show.show_comedians.every((pairing) =>
        isPubliclyVisible(pairing.comedian)
      )
    )
    .map((show) => ({
      id: show.id,
      tixologiEventId: show.tixologi_event_id,
    }));
}

/**
 * Hydrates the page's ids into full shows, preserving the candidate order -
 * `getShowsByIds` returns a Map, and the database's own row order for an
 * `.in()` is not the order we paged in.
 */
async function hydrateInOrder(showIds: string[]): Promise<ShowWithComedian[]> {
  if (showIds.length === 0) {
    return [];
  }

  const showsById = await getShowsByIds(showIds);

  return showIds
    .map((id) => showsById.get(id))
    .filter((show): show is ShowWithComedian => !!show);
}
